import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Modal,
  Button,
  Table,
  Divider,
  Alert,
  Select,
  Form,
  ConfigProvider,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { InvoicingOrderList } from "../../../api/personal";
import "./step1.scss";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { math } from "@/utils/math";
import { IS_MOBILE } from "@/utils/tool";
import { SvgIcon } from "@/components/SvgIcon";
import { SOURCE_ORDER_TYPE } from "@/utils/contants";
export interface IOrderListModalProps {
  hiddenModal?: () => void;
  submitAction: (params) => void;
  orderType: OrderType;
}

const Step1: React.FC<IOrderListModalProps> = ({
  hiddenModal,
  submitAction,
  orderType,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [count, setCount] = useState(0);
  const [orderData, setOrderData] = useState<Array<InvoicingListItem>>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<InvoicingListItem[]>([]);
  const [money, setMoney] = useState(0);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const tipsRef = useRef(null);
  const [form] = Form.useForm();
  const [searchStatus, setSearchStatus] = useState(1);
  const [freeBillMoney, setFreeBillMoney] = useState(0);

  const [sourceOrderType, setSourceOrderType] = useState(orderType);
  const OrderMap = [
    { value: SOURCE_ORDER_TYPE.Apc, label: t("论文APC订单") },
    { value: SOURCE_ORDER_TYPE.Meeting, label: t("会议服务订单") },
    { value: SOURCE_ORDER_TYPE.Ppv, label: t("PPV订单") },
    { value: SOURCE_ORDER_TYPE.Pod, label: t("纸本期刊服务订单") },
    { value: SOURCE_ORDER_TYPE.Ptp, label: t("PTP订单") },
  ];

  const handleSubmit = () => {
    submitAction({
      money,
      orderIds: selectedRowKeys,
      orderType: Number(form.getFieldValue("order_type")),
      //   orderType: selectedOrder[0].publishing_house_order_type,
    });
    hiddenModal();
  };

  const onSelect = (record, selected, selectedRows) => {
    if (selected) {
      setSelectedOrder((pre) => [...pre, record]);
      setSelectedRowKeys((pre) => [...pre, record.order_id]);
    } else {
      let tempSelectedOrder = [...selectedOrder];
      tempSelectedOrder = tempSelectedOrder.filter(
        (item) => item.order_id !== record.order_id,
      );
      setSelectedOrder(tempSelectedOrder);
      const newSelectedRowKeys = tempSelectedOrder.map((item) => item.order_id);
      setSelectedRowKeys(newSelectedRowKeys);
    }
  };
  const onSelectAll = (selected, selectedRows, changeRows) => {
    const changeRowsRowKeys = changeRows.map((item) => item.order_id);
    if (selected) {
      setSelectedOrder((pre) => [...pre, ...changeRows]);
      setSelectedRowKeys((pre) => [...pre, ...changeRowsRowKeys]);
    } else {
      let tempSelectedOrder = [...selectedOrder];
      const filteredArray = tempSelectedOrder.filter(
        (itemA) => !changeRowsRowKeys.includes(itemA.order_id),
      );
      setSelectedOrder(filteredArray);
      const newSelectedRowKeys = filteredArray.map((item) => item.order_id);
      setSelectedRowKeys(newSelectedRowKeys);
    }
  };
  const titleItem = useMemo(() => {
    return {
      [SOURCE_ORDER_TYPE.Meeting]: {
        title: t("会议"),
        dataIndex: "meeting_name",
      },
      [SOURCE_ORDER_TYPE.Bpc]: {
        title: t("图书"),
        dataIndex: "book_title",
      },
    };
  }, [t, sourceOrderType]);

  const columns: ColumnsType<InvoicingListItem> = useMemo(() => {
    return [
      {
        title: t("支付号"),
        dataIndex: "order_id",
      },
      //   {
      //     title: t("期刊"),
      //     dataIndex: "journal_name",
      //   },
      titleItem[sourceOrderType] || {
        title: t("文章"),
        dataIndex: "article_title",
      },
      {
        title: t("可开票金额"),
        dataIndex: "free_bill_money",
        render: (text: number) => `¥ ${text}`,
        width: 160,
      },
      {
        title: t("支付时间"),
        dataIndex: "pay_time",
        width: 140,
        render: (text: number) =>
          `${dayjs(text * 1000).format("YYYY-MM-DD HH:mm:ss")}`,
      },
    ];
  }, [sourceOrderType, t]);

  useEffect(() => {
    const fetchPreOrderType = async () => {
      const orderRes = await InvoicingOrderList<{
        count: number;
        list: Array<InvoicingListItem>;
        n: number;
        p: number;
        free_bill_money: number;
        publishing_house_order_type: OrderType;
      }>({
        p: 1,
        n: 10,
        order_type: orderType,
      });
      if (orderRes.code === 0) {
        const { list, count, free_bill_money } = orderRes.data;
        form.setFieldsValue({
          order_type: orderType,
        });
        if (list.length == 0) {
          setSearchStatus(2);
        } else {
          setFreeBillMoney(free_bill_money);
          const data = list.map((item) => ({
            ...item,
            key: item.order_id,
          }));
          setOrderData(data);
          setCount(count);
        }
      }
    };
    fetchPreOrderType();
  }, [orderType]);

  const fetchOrderList = async () => {
    setLoading(true);
    const orderRes = await InvoicingOrderList<{
      count: number;
      list: Array<InvoicingListItem>;
      n: number;
      p: number;
      free_bill_money: number;
    }>({
      p: pageNumber,
      n: 10,
      order_type: Number(form.getFieldValue("order_type")) as OrderType,
    });
    setLoading(false);
    if (orderRes.code === 0) {
      const { list, count, free_bill_money } = orderRes.data;
      if (list.length == 0) {
        setSearchStatus(2);
      }
      setFreeBillMoney(free_bill_money);
      const data = list.map((item) => ({
        ...item,
        key: item.order_id,
      }));
      setOrderData(data);
      setCount(count);
    }
  };
  useEffect(() => {
    form.getFieldValue("order_type") && fetchOrderList();
  }, [pageNumber]);

  const onChange4Pagination = useCallback((page, pageSize) => {
    setPageNumber(page);
  }, []);

  useEffect(() => {
    const money = selectedOrder.reduce((accumulator, currentItem) => {
      // 使用 math.js 的 round 函数将结果四舍五入为两位小数
      return math.round(
        math.add(accumulator, math.bignumber(currentItem.free_bill_money)),
        2,
      );
    }, math.bignumber(0));

    setMoney(money.toNumber());
  }, [selectedOrder]);

  const handleSearch = async () => {
    handleCancel();
    setPageNumber(1);
    fetchOrderList();
  };

  const handleCancel = async () => {
    // form.resetFields();
    // setDisabled4Search(true);
    setOrderData([]);
    setCount(0);
    // setSearchStatus(1);
    setSelectedOrder([]);
    setSelectedRowKeys([]);
    setFreeBillMoney(0);
  };

  return (
    <Modal
      open
      wrapClassName={`invoicing-step1-modal-wrapper ${
        IS_MOBILE ? "mobile-invoicing-step1-modal-wrapper" : ""
      }`}
      onCancel={hiddenModal}
      footer={
        <>
          <div className="text-wrapper">
            <div className="text1">
              {t("chooseCount", { count: selectedOrder.length })},
            </div>
            <div className="text2">{t("待开票金额")}</div>
            <div className="money">
              <span>¥ </span>
              <span>{money.toFixed(2)}</span>
            </div>
          </div>
          <div className="btn-wrapper">
            <Button
              type="primary"
              onClick={handleSubmit}
              disabled={!Boolean(selectedOrder.length)}
            >
              {t("开具发票")}
            </Button>
          </div>
        </>
      }
      width="83.333rem"
      title={t("申请发票")}
      centered
      maskClosable={false}
      className="invoicing-step1-modal"
    >
      <section className="invoice-tips" ref={tipsRef}>
        <Alert
          message={`${
            sourceOrderType === SOURCE_ORDER_TYPE.Pod
              ? t("发票将在纸本期刊发货后完成开具。")
              : t("发票将在提交后的5个工作日内完成开具。")
          }${t(
            "一旦发票完成开具，我们将通过您提供的电子邮件地址发送通知。如果您有任何疑问，请随时通过 publink@cnpiec.com.cn 与我们联系!",
          )}`}
          type="info"
          showIcon
        />
      </section>
      <div className="invoicing-step1-modal-content">
        <div className="search-wrapper">
          <div className="search">
            <Form layout={"inline"} form={form}>
              <Form.Item label={t("订单类型")} name="order_type">
                <Select
                  style={{ width: 240 }}
                  placeholder={t("请选择")}
                  onChange={(v) => {
                    setSourceOrderType(Number(v) as OrderType);
                    handleSearch();
                  }}
                  options={OrderMap}
                />
              </Form.Item>
              {/* <Form.Item>
                <Button
                  type="primary"
                  disabled={disabled4Search}
                  onClick={handleSearch}
                >
                  {t("查询")}
                </Button>
                <Button style={{ marginLeft: "1rem" }} onClick={handleCancel}>
                  {t("重置")}
                </Button>
              </Form.Item> */}
            </Form>
          </div>
          <div className="money">
            {t("可开票金额")}:
            {freeBillMoney ? `¥ ${freeBillMoney.toFixed(2)}` : "-"}
          </div>
        </div>
        <ConfigProvider
          renderEmpty={() => (
            <div style={{ textAlign: "center" }}>
              <SvgIcon
                icon="icon-zanwuliebiao"
                width={"3.333rem"}
                height={"3.333rem"}
              />
              <p>
                {searchStatus === 1
                  ? t("请先选择订单类型")
                  : t("当前订单类型下暂无可开票账单")}
              </p>
            </div>
          )}
        >
          <Table
            rowSelection={{
              selectedRowKeys,
              onSelect: onSelect,
              onSelectAll: onSelectAll,
            }}
            columns={columns}
            dataSource={orderData}
            bordered
            pagination={{
              position: ["bottomCenter"],
              total: count,
              showTotal: (total) => `${t("totalX", { count: total })}`,
              onChange: onChange4Pagination,
              size: "small",
              current: pageNumber,
            }}
            loading={loading}
            onHeaderRow={(columns) => {
              return {
                className: "header-wrapper",
              };
            }}
          />
        </ConfigProvider>
      </div>
    </Modal>
  );
};

export default Step1;
